import { useMemo } from 'react'
import { resolveSite } from '@/lib/site'
import { useLocale } from '@/lib/locale'
import { localizeCopy } from '@/lib/spelling'
import { CANONICAL_MARKET, type MarketVariants, type SiteVariants } from './types'

type SlideCopyOptions<T> = {
  autoSpelling?: boolean
  skipSpellingFor?: readonly string[]
  /** Per-tenant field overrides, layered on top of the US base copy. */
  site?: SiteVariants<T>
}

/**
 * Resolves a slide's copy for the current face and language.
 *
 * Layering, in order:
 *   1. US base copy (always complete).
 *   2. `QA` market overrides when the face is the Qatar tenant (Gulf-localized
 *      English: riyals, Qatari agencies, local examples).
 *   3. Per-site `site` overrides (e.g. a DCUC military example).
 *   4. British spelling on the Qatar face (Qatar follows UK English), applied
 *      to top-level string fields the same way the GB market does elsewhere.
 *   5. `ar` when the visitor has chosen Arabic. The Arabic block is the full
 *      translation of the QA copy, so it replaces everything above it.
 *
 * Keeps the same `MarketVariants<T>` shape and call signature as GranGuard's
 * useSlideCopy so forked slide copy files compile unchanged. The spelling
 * options are accepted for source compatibility but ignored.
 */
export function useSlideCopy<T extends Record<string, unknown>>(
  copy: MarketVariants<T>,
  options: SlideCopyOptions<T> = {},
): T {
  const siteOverrides = options.site
  const locale = useLocale()
  return useMemo(() => {
    const site = resolveSite()
    let out: T = copy[CANONICAL_MARKET]
    if (site === 'qatar' && copy.QA) out = { ...out, ...copy.QA }
    const override = siteOverrides?.[site]
    if (override) out = { ...out, ...override }
    if (site === 'qatar') out = britishSpelling(out)
    if (locale === 'ar' && copy.ar) out = copy.ar
    return out
  }, [copy, siteOverrides, locale])
}

/** GB spelling over every string (and array-of-string) field, one level deep. */
function britishSpelling<T extends Record<string, unknown>>(copy: T): T {
  const out: Record<string, unknown> = { ...copy }
  for (const [k, v] of Object.entries(out)) {
    if (typeof v === 'string') out[k] = localizeCopy(v, 'GB')
    else if (Array.isArray(v)) {
      out[k] = v.map(item =>
        typeof item === 'string'
          ? localizeCopy(item, 'GB')
          : item && typeof item === 'object'
            ? britishSpelling(item as Record<string, unknown>)
            : item,
      )
    } else if (v && typeof v === 'object') {
      out[k] = britishSpelling(v as Record<string, unknown>)
    }
  }
  return out as T
}
